import { ClipboardCheck } from 'lucide-react';
import type { RequisitionStatus } from '@eombor/contracts';
import { Button, cn, type ButtonVariant } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { isStoppedStatus, transitionActionKey } from '../model/requisition-status.js';

export interface TransitionBarProps {
  /**
   * Shu xodim HOZIR bosa oladigan qadamlar — SERVERDAN keladi
   * (`availableTransitions`).
   *
   * Panel ro'yxatni o'zi hisoblamaydi va hisoblamasligi ham kerak: o'tish
   * qoidalari state machine da, bitta joyda turadi. Bu yerdagi nusxa bir kun
   * albatta undan ajralib ketardi.
   */
  available: readonly RequisitionStatus[];
  /** Hozirgi holat — yorliq va yo'riqnoma shunga qarab tanlanadi. */
  from: RequisitionStatus;
  onTransition: (to: RequisitionStatus) => void;
  /** Hozir bajarilayotgan qadam — o'sha tugma kutish holatida turadi. */
  pending?: RequisitionStatus | null;
  disabled?: boolean;
  className?: string;
  /**
   * Ayrim qadamlar uchun O'Z boshqaruvi.
   *
   * Bitta holat uchun kerak bo'ldi: qabul qilishda tugmaning o'zi fayl
   * maydoni bo'lishi kerak, aks holda kamera faqat IKKINCHI tegishdan
   * keyin ochilardi (avval oyna, keyin oyna ichidagi tugma). Panel esa
   * qaysi qadam qanday boshqaruv talab qilishini bilmasligi kerak —
   * shuning uchun u element sifatida TASHQARIDAN beriladi.
   *
   * Berilgan qadam uchun oddiy tugma umuman chizilmaydi: ikkitasi birga
   * turgan bo'lsa, qaysi biri "haqiqiy" ekani yo'qolardi.
   */
  slots?: Partial<Record<RequisitionStatus, React.ReactNode>>;
}

/**
 * Tugmaning ko'rinishi: oldinga — asosiy, rad etish — xavfli, bekor qilish —
 * xotirjam.
 *
 * Faqat BIRINCHI oldinga qadam to'ldirilgan tugma bo'ladi: ekranda ikki
 * "asosiy" tugma turgan bo'lsa, qaysi biri odatdagi yo'l ekani yo'qoladi.
 */
const variantOf = (
  status: RequisitionStatus,
  isFirstForward: boolean,
): ButtonVariant => {
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED' || status === 'DRAFT') return 'ghost';
  return isFirstForward ? 'primary' : 'secondary';
};

/**
 * Tugmadagi matn — HARAKAT nomi ("Tasdiqlash"), holat nomi emas.
 *
 * Nom o'tishning IKKALA uchiga qaraydi: bir xil holatga o'tish turli
 * qadamlarda turli ish bo'lishi mumkin (`transitionActionKey`).
 */
const actionLabel = (from: RequisitionStatus, to: RequisitionStatus): string =>
  t(transitionActionKey(from, to));

/**
 * Talabnoma kartochkasining pastki paneli — keyingi qadamlar.
 *
 * O'tish yo'q bo'lsa panel umuman chizilmaydi: bo'sh ramka "bu yerda nimadir
 * bo'lishi kerak edi" degan taassurot qoldiradi, holbuki talabnoma shunchaki
 * boshqa xodimni kutmoqda yoki yopilgan.
 */
export const TransitionBar = ({
  available,
  from,
  onTransition,
  pending = null,
  disabled = false,
  className,
  slots,
}: TransitionBarProps) => {
  if (available.length === 0) return null;

  const forward = available.filter((status) => !isStoppedStatus(status));
  const firstForward = forward[0] ?? null;

  return (
    <div
      role="group"
      aria-label={t('requisition.transitionsTitle')}
      className={cn(
        'flex flex-wrap items-center justify-end gap-2 rounded-[var(--radius-lg)]',
        'border border-border bg-surface-2 px-4 py-3',
        className,
      )}
    >
      <span className="mr-auto text-xs text-text-faint">{t('requisition.transitionsHint')}</span>

      {available.map((status) => {
        const slot = slots?.[status];
        if (slot !== undefined) return <span key={status}>{slot}</span>;

        return (
          <Button
            key={status}
            variant={variantOf(status, status === firstForward)}
            loading={pending === status}
            disabled={disabled || (pending !== null && pending !== status)}
            onClick={() => onTransition(status)}
          >
            {actionLabel(from, status)}
          </Button>
        );
      })}
    </div>
  );
};

/**
 * SIZDAN NIMA KUTILMOQDA — sahifaning eng boshida.
 *
 * Tugmadagi ikki so'z ("Tasdiqlash") amalning O'ZINI aytadi, lekin bu
 * qadamda odam nimani tekshirishi kerakligini va undan keyin nima
 * bo'lishini aytmaydi. Har rol o'z qadamida boshqa ish qiladi — boshliq
 * miqdorni belgilaydi, skladchi katakni tanlaydi, buxgalter blankdagi
 * raqamni qo'yadi — va buni hech qayerda o'qib bo'lmasdi.
 *
 * Blok TUGMALARNING yonida emas, sahifaning TEPASIDA: hujjat uzun va
 * tugmalar pastda qoladi, ya'ni ekran ochilganda odam birinchi navbatda
 * jadvalni ko'radi va nima qilish kerakligini bilmaydi. Yo'riqnoma esa
 * ish boshlanishidan OLDIN o'qilishi kerak.
 */
export const NextStepPanel = ({
  available,
  from,
}: {
  available: readonly RequisitionStatus[];
  from: RequisitionStatus;
}) => {
  const next = available.find((status) => !isStoppedStatus(status));
  if (next === undefined) return null;

  return (
    <section
      className={cn(
        'flex items-start gap-3.5 rounded-[var(--radius-lg)] border',
        'border-warn/30 border-l-4 border-l-warn bg-warn-soft px-4 py-3.5',
        'shadow-[var(--shadow-1)]',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'motion-pulse-soft mt-0.5 flex size-9 shrink-0 items-center justify-center',
          'rounded-[var(--radius-md)] bg-warn text-[var(--color-warn-contrast)]',
          '[--pulse-color:var(--color-warn)]',
        )}
      >
        <ClipboardCheck size={17} />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-sm leading-5 font-semibold text-text">
          {t('requisition.todoTitle', { action: actionLabel(from, next) })}
        </span>
        <span className="text-sm leading-6 text-pretty text-text-muted">
          {t(`requisition.todo.${next}`)}
        </span>
      </span>
    </section>
  );
};
